import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuth } from '../context/FirebaseAuthContext';

export function useFirebaseCredits() {
  const { user } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasUnlimitedCredits, setHasUnlimitedCredits] = useState(false);

  useEffect(() => {
    // Check local subscription status for guests
    const checkLocalSubscription = () => {
      const localStatus = localStorage.getItem('subscriptionStatus');
      if (localStatus === 'active') {
        setHasUnlimitedCredits(true);
      }
    };

    checkLocalSubscription();

    if (!user) {
      setCredits(0);
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id || user.uid)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setCredits(data.credits || 0);
          setHasUnlimitedCredits(data.subscription_status === 'premium');
        } else {
          // Create profile if missing
          const userId = user.id || user.uid;
          await supabase.from('user_profiles').insert({
            user_id: userId,
            email: user.email || '',
            credits: 5,
            subscription_status: 'none'
          });
          
          await supabase.from('credit_transactions').insert({
            user_id: userId,
            type: 'bonus',
            amount: 5,
            description: 'Welcome bonus credits',
            related_action: 'user_registration'
          });
          
          setCredits(5);
        }

        // Subscribe to live updates for this user profile using Supabase real-time
        const userId = user.id || user.uid;
        const channel = supabase
          .channel(`user-profile-${userId}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'user_profiles',
              filter: `user_id=eq.${userId}`
            },
            (payload: any) => {
              const profile = payload.new;
              if (profile) {
                setCredits(profile.credits || 0);
                setHasUnlimitedCredits(profile.subscription_status === 'premium');
              }
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      } catch (error) {
        console.error('Error fetching user data from Supabase:', error);
      } finally {
        setLoading(false);
      }
    };

    let unsub: (() => void) | undefined;
    fetchUserData().then((u) => { if (typeof u === 'function') unsub = u });

    return () => {
      if (unsub) unsub();
    };
  }, [user]);

  const spendCredits = async (amount: number) => {
    if (hasUnlimitedCredits) return true;
    if (!user) return false;
    if (credits < amount) return false;

    try {
      const userId = user.id || user.uid;
      const { error } = await supabase.rpc('increment_credits', {
        user_id_param: userId,
        amount_param: -amount
      });

      if (error) {
        // Fallback to manual update if RPC fails
        const { error: manualErr } = await supabase
          .from('user_profiles')
          .update({
            credits: credits - amount,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        if (manualErr) throw manualErr;
      }
      
      setCredits(prev => prev - amount);
      return true;
    } catch (error) {
      console.error('Error spending credits in Supabase:', error);
      return false;
    }
  };

  const addCredits = async (amount: number) => {
    if (!user) return false;

    try {
      const userId = user.id || user.uid;
      const { error } = await supabase.rpc('increment_credits', {
        user_id_param: userId,
        amount_param: amount
      });

      if (error) {
        // Fallback to manual update
        const { error: manualErr } = await supabase
          .from('user_profiles')
          .update({
            credits: credits + amount,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);
        if (manualErr) throw manualErr;
      }
      
      setCredits(prev => prev + amount);
      return true;
    } catch (error) {
      console.error('Error adding credits in Supabase:', error);
      return false;
    }
  };

  const checkCredits = async (amount: number = 1) => {
    if (hasUnlimitedCredits) return true;

    // Check local storage for guest subscription
    const localStatus = localStorage.getItem('subscriptionStatus');
    if (localStatus === 'active') return true;

    return credits >= amount;
  };

  return {
    credits,
    loading,
    hasUnlimitedCredits,
    spendCredits,
    addCredits,
    checkCredits,
  };
}
